import {DefaultButton, PrimaryButton, SpinnerSize} from '@fluentui/react';
import React, {useRef, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import styled from 'styled-components';
import {api, ApiContext} from '../api';
import {Spinner} from '../common/Spinner';
import {colors} from '../styles';
import {FileSelectButton} from '../widgets/FileWidget';
import {
  Buttons,
  cancelLabel,
  FormContainer,
  FormDescription,
} from './CommonForm';
import {MaterialIcon} from '../icons/MaterialIcon';
import {ApiError} from '../ApiError';

type UploadFormProps = {
  appId: string;
  fileTypes?: string[];
  ctx: ApiContext;
  onCancel: () => void;
  onComplete: () => void;
  cancelLabel: string;
  message?: string;
  executeButtonLabel?: string;
};

type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

type UploadState = {
  file: File;
  status: UploadStatus;
  progress: number;
  error?: string;
};

export function UploadForm(props: UploadFormProps) {
  const intl = useIntl();
  const [uploadStates, setUploadStates] = useState<UploadState[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDuplicateFile = (
    newFile: File,
    existingFiles: UploadState[],
  ): boolean => {
    return existingFiles.some(
      (state) =>
        state.file.name === newFile.name &&
        state.file.size === newFile.size &&
        state.file.lastModified === newFile.lastModified,
    );
  };

  const addFiles = (files: File[]) => {
    if (files.length === 0) {
      return;
    }

    const newFiles = files.filter(
      (file) => !isDuplicateFile(file, uploadStates),
    );

    if (newFiles.length === 0) {
      return;
    }

    const newStates: UploadState[] = newFiles.map((file) => ({
      file,
      status: 'pending',
      progress: 0,
    }));

    setUploadStates((prev) => [...prev, ...newStates]);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    addFiles(files);
    event.target.value = '';
  };

  const upload = async (file: File) => {
    await api.postFormData(
      props.ctx,
      `/app/${props.appId}/upload`,
      {},
      {file: [file]},
    );
  };

  const updateUploadState = (index: number, updates: Partial<UploadState>) => {
    setUploadStates((prev) =>
      prev.map((state, i) => (i === index ? {...state, ...updates} : state)),
    );
  };

  const startUpload = async () => {
    if (uploadStates.length === 0 || isUploading) return;

    setIsUploading(true);

    for (let i = 0; i < uploadStates.length; i++) {
      const state = uploadStates[i];

      if (state.status !== 'pending') continue;

      try {
        updateUploadState(i, {status: 'uploading', progress: 0});

        await upload(state.file);

        updateUploadState(i, {status: 'completed', progress: 100});
      } catch (error) {
        updateUploadState(i, {
          status: 'error',
          error:
            error instanceof ApiError
              ? error.getMessage()
              : error instanceof Error
              ? error.message
              : intl.formatMessage({
                  id: 'Upload.Form.Error.Unknown',
                  defaultMessage: 'Failed to upload',
                }),
        });
      }
    }

    setIsUploading(false);

    if (props.onComplete) {
      props.onComplete();
    }
  };

  const removeFile = (index: number) => {
    setUploadStates((prev) => prev.filter((_, i) => i !== index));
  };

  const completedCount = uploadStates.filter(
    (state) => state.status === 'completed',
  ).length;

  const totalCount = uploadStates.length;

  const onCancel = () => {
    props.onCancel();
  };

  const cancelButton = (
    <DefaultButton
      onClick={onCancel}
      text={cancelLabel(props.cancelLabel, intl)}
    />
  );

  return (
    <FormContainer style={{width: '100%'}}>
      <FormDescription>
        {props.message ||
          intl.formatMessage({
            id: 'Upload.Form.Description',
            defaultMessage: 'Please select files to upload.',
          })}
      </FormDescription>
      <FileSelectButton input={fileInputRef} />
      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple={true}
        accept={props.fileTypes?.join(',') || '*/*'}
        onChange={handleFileSelect}
        disabled={isUploading}
      />

      {uploadStates.length > 0 && (
        <div>
          <StatesContainer>
            {uploadStates.map((state, index) => (
              <StateContainer
                key={index}
                style={{
                  color: selectColor(state.status),
                  borderBottom:
                    index < uploadStates.length - 1 ? '1px solid #eee' : 'none',
                }}>
                <StateInfo>
                  <StatusIconContainer>
                    <StatusIcon status={state.status} />
                  </StatusIconContainer>
                  <FilenameContainer>
                    <Filename filename={state.file.name} />
                    <RemoveButton
                      status={state.status}
                      onClick={() => removeFile(index)}
                    />
                  </FilenameContainer>
                  <StatusTextContainer>
                    <StatusText status={state.status} intl={intl} />
                  </StatusTextContainer>
                </StateInfo>
                {state.error && <ErrorText>{state.error}</ErrorText>}
              </StateContainer>
            ))}
          </StatesContainer>
          <div>
            {intl.formatMessage({
              id: 'Upload.Form.Progress',
              defaultMessage: 'Progress',
            })}
            : {completedCount} / {totalCount}{' '}
            {intl.formatMessage({
              id: 'Upload.Form.Progress.Completed',
              defaultMessage: 'Completed',
            })}
          </div>
        </div>
      )}

      <Buttons>
        <PrimaryButton
          disabled={
            isUploading ||
            !uploadStates.some((state) => state.status === 'pending')
          }
          onClick={startUpload}
          text={
            props.executeButtonLabel ||
            intl.formatMessage({
              id: 'Action.Upload',
              defaultMessage: 'Upload',
            })
          }
          styles={{root: {marginRight: '1rem'}}}
        />
        {cancelButton}
      </Buttons>
    </FormContainer>
  );
}

type StatusIconProps = {
  status: UploadStatus;
};

function StatusIcon(props: StatusIconProps): JSX.Element | null {
  switch (props.status) {
    case 'pending':
      return null;
    case 'uploading':
      return <Spinner size={SpinnerSize.xSmall} />;
    case 'completed':
      return <MaterialIcon iconName={'check'} />;
    case 'error':
      return <MaterialIcon iconName={'error_outline'} />;
    default:
      return null;
  }
}

type StatusTextProps = {
  status: UploadStatus;
  intl: IntlShape;
};

function StatusText(props: StatusTextProps): JSX.Element | null {
  const text = selectStatusText(props.status, props.intl);
  return <StatusTextElem>{text}</StatusTextElem>;
}

function selectStatusText(status: UploadStatus, intl: IntlShape): string {
  switch (status) {
    case 'pending':
      return intl.formatMessage({
        id: 'Upload.Form.Status.Pending',
        defaultMessage: 'Pending',
      });
    case 'uploading':
      return intl.formatMessage({
        id: 'Upload.Form.Status.Uploading',
        defaultMessage: 'Uploading',
      });
    case 'completed':
      return intl.formatMessage({
        id: 'Upload.Form.Status.Completed',
        defaultMessage: 'Completed',
      });
    case 'error':
      return intl.formatMessage({
        id: 'Upload.Form.Status.Error',
        defaultMessage: 'Error',
      });
    default:
      return intl.formatMessage({
        id: 'Upload.Form.Status.Pending',
        defaultMessage: 'Pending',
      });
  }
}

type FilenameProps = {
  filename: string;
};

function Filename(props: FilenameProps): JSX.Element {
  return <FilenameText title={props.filename}>{props.filename}</FilenameText>;
}

type RemoveButtonProps = {
  status: UploadStatus;
  onClick: () => void;
};

function RemoveButton(props: RemoveButtonProps): JSX.Element | null {
  if (props.status === 'uploading') {
    return null;
  }

  return (
    <RemoveButtonWrapper onClick={props.onClick}>
      <MaterialIcon iconName={'close'} />
    </RemoveButtonWrapper>
  );
}

function selectColor(status: UploadStatus): string {
  switch (status) {
    case 'error':
      return colors.textColorError;
  }

  return colors.textColor;
}

const HiddenInput = styled.input`
  display: none;
`;

const StatesContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
  border: 1px solid ${colors.borderColorLightest};
  border-radius: 4px;
  padding: 0 0.5rem;
  background-color: ${colors.bodyColor};
`;

const StateContainer = styled.div`
  padding: 0.5rem 0;
`;

const StateInfo = styled.div`
  display: flex;
  align-items: center;
`;

const StatusIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 0.5rem;
`;

const FilenameContainer = styled.div`
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
`;

const FilenameText = styled.div`
  font-size: 0.9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 30rem;
`;

const StatusTextContainer = styled.div`
  text-align: right;
`;

const StatusTextElem = styled.div`
  font-size: 0.9rem;
`;

const ErrorText = styled.div`
  font-size: 0.9rem;
  margin-top: 0.1rem;
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  color: ${colors.textColorTheme};
`;
